import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import UpcomingMenu from 'models/UpcomingMenu';
import type { IUpcomingMenu } from 'models/UpcomingMenu';
import FetchState from 'models/FetchState';

type Props = { children: ReactNode; };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'reload'; data: IUpcomingMenu[]; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  upcomingMenus: UpcomingMenu[];
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const UpcomingMenusContext = createContext<ContextValue | null>(null);

function upcomingMenusReducer(value: ContextValue, action: Action): ContextData {
  const { upcomingMenus, fetchState } = value;

  switch (action.type) {
    case 'reload': {
      return {
        fetchState,
        upcomingMenus: action.data.map((menu) => new UpcomingMenu(menu)),
      };
    }

    case 'setFetchState': {
      return { upcomingMenus, fetchState: action.data };
    }

    default: {
      return value;
    }
  }
}

function UpcomingMenusProvider({ children }: Props): ReactElement {
  const [data, dispatch] = useReducer(upcomingMenusReducer, {
    fetchState: FetchState.IN_PROGRESS,
    upcomingMenus: [],
  });

  const { upcomingMenus, fetchState } = data;

  return (
    <UpcomingMenusContext.Provider value={{ upcomingMenus, dispatch, fetchState }}>
      {children}
    </UpcomingMenusContext.Provider>
  );
}

export type { ContextValue };
export { UpcomingMenusContext, UpcomingMenusProvider };
